export const VISITOR_COOKIE = "_vid";

import { UMAMI_TRACKING_DOMAINS } from "./analytics-domains";
import {
  isListmonkVisitorId,
  LISTMONK_VISITOR_COOKIE,
  LISTMONK_VISITOR_PARAM,
  resolveListmonkVisitorId,
} from "./listmonk-visitor";
export { UMAMI_TRACKING_DOMAINS } from "./analytics-domains";
export {
  LISTMONK_VISITOR_COOKIE,
  LISTMONK_VISITOR_PARAM,
  isListmonkVisitorId,
  resolveListmonkVisitorId,
} from "./listmonk-visitor";

const COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

export type AnalyticsEventData = Record<
  string,
  string | number | boolean | undefined
>;

declare global {
  interface Window {
    umami?: {
      track: (event: string, data?: AnalyticsEventData) => void;
      identify: (
        id: string | { id?: string; [key: string]: string | number | boolean | undefined },
        data?: AnalyticsEventData,
      ) => void;
    };
  }
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, domain?: string): void {
  if (typeof document === "undefined") return;
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `path=/`,
    `max-age=${COOKIE_MAX_AGE_SECONDS}`,
    `SameSite=Lax`,
  ];
  if (domain) parts.push(`domain=${domain}`);
  if (typeof window !== "undefined" && window.location.protocol === "https:") {
    parts.push("Secure");
  }
  document.cookie = parts.join("; ");
}

function createVisitorId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `v-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function getVisitorCookieDomain(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return undefined;
  if (host === "interactggz.nl" || host.endsWith(".interactggz.nl")) {
    return ".interactggz.nl";
  }
  return undefined;
}

export function getOrCreateVisitorId(): string {
  const existing = readCookie(VISITOR_COOKIE);
  if (existing) return existing;

  const visitorId = createVisitorId();
  writeCookie(VISITOR_COOKIE, visitorId, getVisitorCookieDomain());
  return visitorId;
}

export function getUmamiScriptUrl(): string {
  const fromEnv = import.meta.env.VITE_UMAMI_SCRIPT_URL as string | undefined;
  return fromEnv || "https://analytics.interactggz.nl/script.js";
}

export function getUmamiWebsiteId(): string | undefined {
  const fromEnv = import.meta.env.VITE_UMAMI_WEBSITE_ID as string | undefined;
  return fromEnv?.trim() || undefined;
}

/** Remember `?lm=` from a Listmonk link so later pageviews stay tied to that subscriber. */
export function persistListmonkVisitorId(): string | null {
  if (typeof window === "undefined") return null;
  const fromQuery = new URLSearchParams(window.location.search).get(LISTMONK_VISITOR_PARAM);
  const id = resolveListmonkVisitorId(
    window.location.search,
    readCookie(LISTMONK_VISITOR_COOKIE),
  );
  if (isListmonkVisitorId(fromQuery)) {
    writeCookie(LISTMONK_VISITOR_COOKIE, fromQuery, getVisitorCookieDomain());
  }
  return id;
}

let scriptLoadPromise: Promise<void> | null = null;

export function loadUmamiScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  const listmonkId = persistListmonkVisitorId();
  if (window.umami) {
    if (listmonkId) window.umami.identify(listmonkId);
    return Promise.resolve();
  }

  const websiteId = getUmamiWebsiteId();
  if (!websiteId) return Promise.resolve();

  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-website-id][src*="script.js"]',
    );
    if (existing) {
      if (listmonkId && !existing.dataset.distinctId) existing.dataset.distinctId = listmonkId;
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Umami script failed")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.defer = true;
    script.src = getUmamiScriptUrl();
    script.dataset.websiteId = websiteId;
    script.dataset.domains = UMAMI_TRACKING_DOMAINS;
    if (listmonkId) script.dataset.distinctId = listmonkId;
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("Umami script failed")), {
      once: true,
    });
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

export async function initAnalytics(): Promise<void> {
  getOrCreateVisitorId();
  await loadUmamiScript();
}

export function trackEvent(name: string, data: AnalyticsEventData = {}): void {
  const vid = getOrCreateVisitorId();
  const lm = persistListmonkVisitorId();
  window.umami?.track(name, { ...data, vid, ...(lm ? { lm } : {}) });
}

export function identifyUser(hashedUserId: string): void {
  window.umami?.identify(hashedUserId);
}

let toolSessionStartedAt: number | null = null;

export function trackToolOpen(
  slug: string,
  access: string,
  extra?: AnalyticsEventData,
): void {
  toolSessionStartedAt = Date.now();
  trackEvent("tool_open", { slug, access, ...extra });
}

export function trackToolClose(slug: string): void {
  const durationMs =
    toolSessionStartedAt === null ? 0 : Date.now() - toolSessionStartedAt;
  trackEvent("tool_close", { slug, durationMs });
  toolSessionStartedAt = null;
}

export function bindToolCloseTracking(slug: string): () => void {
  const onClose = () => trackToolClose(slug);
  window.addEventListener("beforeunload", onClose);
  window.addEventListener("pagehide", onClose);
  return () => {
    window.removeEventListener("beforeunload", onClose);
    window.removeEventListener("pagehide", onClose);
  };
}
